import { buildPortalProtocolTicketUrl } from "./portal-urls";
import { API_HOST, API_PATH, API_PREFIX_PATH } from "../constants";
import { Brain } from "./Brain";
import type { RequestParams } from "./http-client";
import { ApiError } from "./api-error";

export { ApiError, apiErrorDetail, isUnauthorized } from "./api-error";

const isDeployedToCustomApiPath = API_PREFIX_PATH !== API_PATH;

const constructBaseUrl = (): string => {
  if (isDeployedToCustomApiPath) {
    const domain = window.location.origin || `https://${API_HOST}`;
    return `${domain}${API_PREFIX_PATH}`;
  }
  return `https://${API_HOST}${API_PATH}`;
};

type BaseApiParams = Omit<RequestParams, "signal" | "baseUrl" | "cancelToken">;

const constructBaseApiParams = (): BaseApiParams => {
  return {
    credentials: "include",
    secure: true,
  };
};

const constructClient = () => {
  const baseUrl = constructBaseUrl();
  const baseApiParams = constructBaseApiParams();

  return new Brain({
    baseUrl,
    baseApiParams,
    customFetch: async (url, options) => {
      let fetchUrl: RequestInfo | URL = url;
      if (isDeployedToCustomApiPath) {
        const urlString = typeof url === 'string' ? url : url.toString();
        fetchUrl = urlString.replace(API_PREFIX_PATH + "/routes", API_PREFIX_PATH);
      }

      const response = await fetch(fetchUrl, options);

      if (!response.ok) {
        const text = await response.text();

        // Auto-retry once on CSRF failure. The 403 response sets a fresh
        // CSRF cookie (Set-Cookie header processed before we reach here), so
        // reading it right away and retrying the request is safe.
        if (response.status === 403) {
          try {
            if (JSON.parse(text).detail === 'CSRF validation failed') {
              let freshToken: string | null = null;
              for (const cookie of document.cookie.split(';')) {
                const eq = cookie.indexOf('=');
                if (eq > -1 && cookie.substring(0, eq).trim() === 'csrf_token') {
                  freshToken = decodeURIComponent(cookie.substring(eq + 1).trim());
                  break;
                }
              }
              if (freshToken) {
                const retryHeaders: Record<string, string> = {
                  ...((options?.headers as Record<string, string>) || {}),
                  'X-CSRF-Token': freshToken,
                };
                const retryResponse = await fetch(fetchUrl, { ...options, headers: retryHeaders });
                if (retryResponse.ok) return retryResponse;
                const retryText = await retryResponse.text(); // consume body before throwing
                throw new ApiError(retryResponse.status, retryResponse.statusText, retryText);
              }
            }
          } catch (retryErr) {
            if (retryErr instanceof ApiError) throw retryErr;
            // JSON.parse or cookie read failed — fall through to original error
          }
        }

        throw new ApiError(response.status, response.statusText, text);
      }

      return response;
    },
  });
};

// Endpoints not (yet) present in the generated client (src/brain/Brain.ts) get patched on
// here by hand. This interface documents their real shape so callers don't need `any`.
interface BrainExtensions {
  notify_premounted: (params: { order_id: number }) => Promise<Response>;
  get_tenant_subscription: (params: { tenantId: number }) => Promise<Response>;
  export_customers: (params: { search?: string; no_email?: boolean }) => Promise<Response>;
  list_tire_set_photos: (tireSetId: number) => Promise<Response>;
  upload_tire_set_photo: (tireSetId: number, file: File, position?: string | null) => Promise<Response>;
  delete_tire_set_photo: (photoId: number) => Promise<Response>;
}

const brain = constructClient() as Brain & BrainExtensions;

// Manual addition for notify_premounted endpoint (until backend is restarted)
brain.notify_premounted = async (params: { order_id: number }) => {
  const baseUrl = constructBaseUrl();
  const url = `${baseUrl}/orders/notify-premounted`;

  const getCsrfToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  // ✅ httpOnly cookie is sent automatically
  const csrfToken = getCsrfToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // ✅ No Authorization header needed - cookie is sent automatically

  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(params),
  });

  return response;
};

// Manual addition for subscription endpoints
brain.get_tenant_subscription = async (params: { tenantId: number }) => {
  const baseUrl = constructBaseUrl();
  const url = `${baseUrl}/tenants/${params.tenantId}/subscription`;

  // ✅ httpOnly cookie is sent automatically
  const headers: Record<string, string> = {};

  // ✅ No Authorization header needed - cookie is sent automatically

  const response = await fetch(url, {
    method: "GET",
    headers,
    credentials: "include",
  });

  return response;
};


brain.export_customers = async (params: { search?: string; no_email?: boolean }) => {
  const baseUrl = constructBaseUrl();
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.no_email) query.set('no_email', 'true');
  const url = `${baseUrl}/customers/export${query.toString() ? '?' + query.toString() : ''}`;
  return fetch(url, { method: 'GET', credentials: 'include' });
};

// Tire set photo helpers
export const getTireSetPhotoServeUrl = (photoId: number): string => {
  const baseUrl = constructBaseUrl();
  return `${baseUrl}/tire_set_photos/serve/${photoId}`;
};

/** Adresse eines Protokoll-PDFs über ein kurzlebiges Ticket (keine Zugangsdaten in der Adresse). */
export const getPortalProtocolTicketUrl = (ticket: string): string =>
  buildPortalProtocolTicketUrl(constructBaseUrl(), ticket);

const getCsrfTokenFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') return decodeURIComponent(value);
  }
  return null;
};

brain.list_tire_set_photos = async (tireSetId: number) => {
  const baseUrl = constructBaseUrl();
  return fetch(`${baseUrl}/tire_set_photos/${tireSetId}`, { method: 'GET', credentials: 'include' });
};

brain.upload_tire_set_photo = async (tireSetId: number, file: File, position?: string | null) => {
  const baseUrl = constructBaseUrl();
  const query = position ? `?position=${position}` : '';
  const csrfToken = getCsrfTokenFromCookie();
  const headers: Record<string, string> = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const formData = new FormData();
  formData.append('file', file);
  return fetch(`${baseUrl}/tire_set_photos/${tireSetId}/upload${query}`, {
    method: 'POST', headers, credentials: 'include', body: formData,
  });
};

brain.delete_tire_set_photo = async (photoId: number) => {
  const baseUrl = constructBaseUrl();
  const csrfToken = getCsrfTokenFromCookie();
  const headers: Record<string, string> = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  return fetch(`${baseUrl}/tire_set_photos/${photoId}`, {
    method: 'DELETE', headers, credentials: 'include',
  });
};

export default brain;
